import type { BackofficeTranslations } from './backoffice.interface';

export const backofficeTranslations: BackofficeTranslations = {
  nav_section_title: 'Internal console',
  nav_overview: 'Overview',
  nav_organizations: 'Organizations',
  nav_users: 'Users',
  nav_cards_cases: 'Reconciliation',
  nav_cards_calls: 'Provider calls',

  error_generic: 'We could not complete the action.',
  retry: 'Retry',
  filters_label: 'Filters',
  filters_clear: 'Clear filters',
  never: 'Never',
  actions_label: 'Actions',

  overview_title: 'Overview',
  overview_subtitle: 'How {{brand}} is doing today, across every organization.',
  kpi_organizations: 'Organizations',
  kpi_users: 'Customer users',
  kpi_internal_users: 'Internal users',
  kpi_signups_7: 'Sign-ups · 7 days',
  kpi_signups_30: 'Sign-ups · 30 days',
  subscriptions_title: 'Subscriptions by status',
  subscriptions_empty: 'No subscriptions yet.',
  generated_at: 'Computed on {{date}}',
  analytics_soon_badge: 'Coming soon',
  analytics_soon_title: 'Product analytics',
  analytics_soon_description:
    'Funnels, retention and usage per organization, from PostHog. This view is the base to plug them into.',

  subscription_status_active: 'Active',
  subscription_status_trialing: 'Trialing',
  subscription_status_past_due: 'Past due',
  subscription_status_cancelled: 'Cancelled',

  status_active: 'Active',
  status_disabled: 'Disabled',

  orgs_title: 'Organizations',
  orgs_subtitle: 'Every {{brand}} organization.',
  orgs_search_placeholder: 'Search by a member’s email or name',
  filter_status_label: 'Status',
  filter_status_all: 'All statuses',
  col_owner: 'Owner',
  col_status: 'Status',
  col_members: 'Members',
  col_subscription: 'Subscription',
  col_created: 'Created',
  no_subscription: 'No subscription',
  owner_unknown: 'No members',
  orgs_empty_title: 'No organizations',
  orgs_empty_description: 'Try another search or clear the filters.',

  org_detail_title: 'Organization',
  org_members_title: 'Members',
  org_field_id: 'ID',
  org_field_created: 'Created',
  org_field_status: 'Status',
  org_field_plan: 'Plan',
  org_field_period_end: 'Period ends',
  org_field_trial_end: 'Trial ends',
  org_field_members: 'Members',

  users_title: 'Users',
  users_subtitle: 'Every {{brand}} account, customers and staff.',
  users_search_placeholder: 'Search by email or name',
  filter_type_label: 'Type',
  filter_type_all: 'All types',
  type_user: 'Customer',
  type_internal_user: 'Internal',
  col_user: 'User',
  col_type: 'Type',
  col_organization: 'Organization',
  col_last_login: 'Last login',
  users_empty_title: 'No users',
  users_empty_description: 'Try another search or clear the filters.',

  action_impersonate: 'Sign in as this user',
  action_promote: 'Make internal',
  action_view_organization: 'View organization',

  impersonate_title: 'Sign in as {{email}}',
  impersonate_description:
    'This link opens a session in the customer’s account. It works once, and who opened it is recorded.',
  impersonate_link_label: 'Access link',
  impersonate_copy: 'Copy link',
  impersonate_copied: 'Copied',
  impersonate_expires: 'Expires at {{time}}.',
  impersonate_private_hint:
    'Open it in a private window: in this window it would replace your internal session.',
  impersonate_close: 'Close',

  promote_title: 'Make {{email}} internal?',
  promote_description:
    'They leave their organization, every session they have is closed, and next time they sign in they see this console. This cannot be undone from here.',
  promote_confirm: 'Make internal',
  promote_cancel: 'Cancel',
  promote_success: 'They are now an internal user.',

  error_LAST_ACTIVE_MEMBER:
    'They are the only active member of their organization, which would be left with nobody who can sign in. Create a new internal account instead.',
  error_ALREADY_INTERNAL: 'This account is already internal.',
  error_USER_INACTIVE: 'The account is disabled.',
  error_TARGET_NOT_CUSTOMER: 'You can only sign in to customer accounts.',

  cases_title: 'Reconciliation',
  cases_subtitle: 'Gaps between our ledger and the provider that someone has to review.',
  filter_case_status_label: 'Status',
  filter_case_status_all: 'All statuses',
  case_status_open: 'Open',
  case_status_investigating: 'Investigating',
  case_status_resolved: 'Resolved',
  case_status_dismissed: 'Dismissed',
  case_kind_balance_mismatch: 'Balance mismatch',
  case_kind_negative_balance: 'Negative balance',
  case_kind_transfer_stuck: 'Stuck transfer',
  case_kind_unknown_event: 'Unknown event',
  case_kind_provider_account_missing: 'Account missing at the provider',
  case_kind_provider_deleted: 'Deleted at the provider',
  col_case_kind: 'Kind',
  col_case_status: 'Status',
  col_case_organization: 'Organization',
  col_case_residual: 'Gap',
  col_case_runs: 'Runs',
  col_case_hold: 'Hold',
  col_case_created: 'Opened',
  hold_yes: 'Frozen',
  hold_no: 'No hold',
  cases_empty_title: 'No cases',
  cases_empty_description: 'No open gaps with these filters.',
  action_investigate: 'Mark investigating',
  action_resolve: 'Resolve',
  investigate_success: 'Case marked as investigating.',
  resolve_title: 'Resolve case',
  resolve_description:
    'Reconciliation runs again before closing: if the gap is still major, the case stays open.',
  resolve_type_label: 'Resolution',
  resolve_type_dismiss: 'Dismiss (it was not a real gap)',
  resolve_type_provider_corrected: 'The provider corrected it',
  resolve_note_label: 'Note',
  resolve_note_placeholder: 'What you found and why it is closed',
  resolve_restore_label: 'Reactivate cards',
  resolve_restore_description: 'Unfreeze the cards that were active before the hold.',
  resolve_confirm: 'Resolve',
  resolve_cancel: 'Cancel',
  resolve_success: 'Case resolved.',

  calls_title: 'Provider calls',
  calls_subtitle: 'Audit trail of every call to the card provider, without sensitive data.',
  calls_operation_placeholder: 'Operation, e.g. accounts.batchTransfer',
  filter_outcome_label: 'Outcome',
  filter_outcome_all: 'All outcomes',
  outcome_ok: 'OK',
  outcome_error: 'Error',
  col_call_operation: 'Operation',
  col_call_outcome: 'Outcome',
  col_call_organization: 'Organization',
  col_call_targets: 'Resources',
  col_call_created: 'Date',
  calls_empty_title: 'No calls',
  calls_empty_description: 'No calls with these filters.',
};
